import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Combine, Loader2 } from 'lucide-react';

export default function MergeDialog({ open, onOpenChange, onConfirm, selectedInvoices, totalAmount, totalHours, loading }) {
  const emails = [...new Set(selectedInvoices.map(inv => inv.client_email).filter(Boolean))];
  const emailMismatch = emails.length > 1;
  const defaultEmail = selectedInvoices[0]?.client_email || '';

  const [mergeEmail, setMergeEmail] = useState(defaultEmail);

  // Reset email when dialog opens or selection changes
  useEffect(() => {
    if (open) {
      setMergeEmail(selectedInvoices[0]?.client_email || '');
    }
  }, [open, selectedInvoices]);

  const handleConfirm = () => {
    onConfirm(mergeEmail);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle data-role="heading">Merge {selectedInvoices.length} Invoice Items</DialogTitle>
          <DialogDescription>
            This will create a new combined invoice item and mark the originals as merged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {emailMismatch && (
            <div className="p-3 rounded-md bg-[hsl(var(--warning))]/10 border border-[hsl(var(--warning))]/30">
              <p className="text-sm font-medium text-[hsl(var(--warning))]">
                Note: Selected items have different client emails
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {emails.join(', ')}
              </p>
            </div>
          )}

          <div>
            <label htmlFor="merge-email" className="text-xs font-medium text-muted-foreground mb-1.5 block">
              Client Email for Merged Item
            </label>
            <Input
              id="merge-email"
              type="email"
              value={mergeEmail}
              onChange={(e) => setMergeEmail(e.target.value)}
              placeholder="client@example.com"
              className="h-9"
              data-testid="merge-email-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-muted">
              <p className="text-xs text-muted-foreground mb-1">Total Hours</p>
              <p className="text-lg font-semibold" data-role="mono">{totalHours.toFixed(2)}h</p>
            </div>
            <div className="p-3 rounded-lg bg-muted">
              <p className="text-xs text-muted-foreground mb-1">Total Amount</p>
              <p className="text-lg font-semibold" data-role="mono">${totalAmount.toFixed(2)}</p>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">Combined Descriptions</p>
            <div className="p-3 rounded-lg bg-muted text-sm max-h-40 overflow-y-auto">
              {selectedInvoices.map((inv, i) => (
                <p key={inv.id} className="mb-1">
                  {inv.description || <em className="text-muted-foreground">No description</em>}
                  {i < selectedInvoices.length - 1 && <span className="text-muted-foreground"> |</span>}
                </p>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={loading}
            className="transition-colors duration-150 active:scale-[0.98]"
            data-testid="merge-confirm-button"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Combine className="h-4 w-4 mr-1" />
            )}
            Confirm Merge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
